import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './EventListing.module.scss';

import { EventCard, EventTypeData, LocationType } from './Types/EventListing.Type';
import classNames from 'classnames/bind';
import { useI18n } from 'next-localization';
import Icon from 'components/helpers/Icon';
import { useRouter } from 'next/router';
import Button from 'components/helpers/Button/Button';
import { removeFromFilter, useEventTypeFilter } from './EventListingUtils';
import { LayoutServicePageState, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { getCleanID } from 'lib/utils';

export type EventListingFiltersProps = {
  eventsToShow: string;
  eventCards: EventCard[];
  onFilterChangeCallback: (eventTypes: EventTypeData[], locationTypes: EventTypeData[]) => void;
  onDateFilterChangeCallback: (dateFilter: string) => void;
  showNoResultsFound: boolean;
  locationTypeFilters: LocationType[];
  headerTitle: string;
};

const getEventTypes = (eventCards: EventCard[]): EventTypeData[] => {
  const eventTypeMap = new Map<string, EventTypeData>();

  eventCards.forEach((card) => {
    const typeId = card.event.type?.targetItem?.id;
    const typeName = card.event.type?.targetItem?.eventType?.value;

    if (typeName && typeId && !eventTypeMap.has(typeId)) {
      eventTypeMap.set(typeId, {
        id: typeId,
        name: String(typeName),
      });
    }
  });

  return Array.from(eventTypeMap.values()).sort((a, b) => a.name.localeCompare(b.name));
};

const getLocationTypes = (locationTypeFilters: LocationType[], eventcards: EventCard[]): EventTypeData[] => {
  const locationTypeMap = new Map<string, EventTypeData>();
  if (locationTypeFilters?.length > 0) {
    locationTypeFilters.forEach((locationType) => {
      const locationId = getCleanID(locationType?.id);
      const locationName = locationType?.phrase;

      if (locationId && locationName && !locationTypeMap.has(locationId)) {
        locationTypeMap.set(locationId, {
          id: locationId,
          name: String(locationName),
        });
      }
    });
  } else {
    eventcards.forEach((card) => {
      const locationTypes = card.event.locationType.targetItems;
      if (Array.isArray(locationTypes)) {
        locationTypes.forEach((locationType) => {
          const locationId = locationType?.id;
          const locationName = locationType?.name;

          if (locationId && locationName && !locationTypeMap.has(locationId)) {
            locationTypeMap.set(locationId, {
              id: locationId,
              name: String(locationName),
            });
          }
        });
      }
    });
  }

  return Array.from(locationTypeMap.values()).sort((a, b) => a.name.localeCompare(b.name));
};

const EventListingFilters: React.FC<EventListingFiltersProps> = (props) => {
  const router = useRouter();
  const { t } = useI18n();
  const { sitecoreContext } = useSitecoreContext();

  const dateFilters = [
    { key: 'today', value: t('Today') },
    { key: 'thisWeek', value: t('ThisWeek') },
    { key: 'nextWeek', value: t('NextWeek') },
    { key: 'thisMonth', value: t('ThisMonth') },
  ];
  const [selectedDateFilter, setSelectedDateFilter] = useState('');
  const [selectedEventTypeFilter, setSelectedEventTypeFilter] = useState<EventTypeData[]>([]);
  const [selectedLocationTypeFilter, setSelectedLocationTypeFilter] = useState<EventTypeData[]>([]);
  const {
    eventsToShow,
    eventCards,
    onFilterChangeCallback,
    onDateFilterChangeCallback,
    showNoResultsFound,
    locationTypeFilters,
    headerTitle,
  } = props;
  const hasAppliedQueryEventTypes = useRef(false);

  const eventTypes = useMemo(() => getEventTypes(eventCards), [eventCards]);
  const locationTypes = useMemo(
    () => getLocationTypes(locationTypeFilters, eventCards),
    [eventCards, locationTypeFilters]
  );

  const handleDateFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const filter = e.target?.value;
    setSelectedDateFilter(filter);
    onDateFilterChangeCallback(filter);
    updateFiltersInUrl(selectedEventTypeFilter, selectedLocationTypeFilter, filter);
  };

  const cx = classNames.bind(styles);
  const { updateFiltersInUrl } = useEventTypeFilter(router);
  const [filtersActive, setFiltersActive] = useState(false);
  const [filtersToShow, setFiltersToShow] = useState(5);
  const [viewAllTypes, setViewAllTypes] = useState(false);

  const showViewMore = eventTypes.length > 5;

  const onViewMoreClick = () => {
    const typesToShow = !viewAllTypes ? eventTypes.length : 5;
    setViewAllTypes(!viewAllTypes);
    setFiltersToShow(typesToShow);
  };

  const updateFilters = (eventTypes: EventTypeData[], locationTypes: EventTypeData[]) => {
    onFilterChangeCallback(eventTypes, locationTypes);
    updateFiltersInUrl(eventTypes, locationTypes, selectedDateFilter);
  };

  const onClearFilters = () => {
    setSelectedDateFilter('');
    setSelectedEventTypeFilter([]);
    setSelectedLocationTypeFilter([]);
    updateFilters([], []);
    onDateFilterChangeCallback('');
    updateFiltersInUrl([], [], '');
  };

  const removeDateFilter = () => {
    setSelectedDateFilter('');
    onDateFilterChangeCallback('');
    updateFiltersInUrl(selectedEventTypeFilter, selectedLocationTypeFilter, '');
  };

  useEffect(() => {
    if (
      hasAppliedQueryEventTypes.current ||
      !router.isReady ||
      sitecoreContext.pageState !== LayoutServicePageState.Normal ||
      !eventTypes.length ||
      !locationTypes.length
    )
      return;
    hasAppliedQueryEventTypes.current = true;
    if (router.asPath?.indexOf('?') > 0) {
      const queryEventTypes = router.query.eventType as string | undefined;
      const queryLocationTypes = router.query.locationType as string | undefined;
      const queryDateFilter = router.query.date as string | undefined;

      const matchedEventTypes = queryEventTypes
        ? eventTypes.filter((et) => queryEventTypes.split(',').includes(et.id))
        : [];

      const matchedLocationTypes = queryLocationTypes
        ? locationTypes.filter((lt) => queryLocationTypes.split(',').includes(lt.id))
        : [];

      if (matchedEventTypes.length || matchedLocationTypes.length) {
        setSelectedEventTypeFilter(matchedEventTypes);
        setSelectedLocationTypeFilter(matchedLocationTypes);
        onFilterChangeCallback(matchedEventTypes, matchedLocationTypes);
      }

      setSelectedDateFilter(queryDateFilter ?? '');
      onDateFilterChangeCallback(queryDateFilter ?? '');
    }
  }, [
    router.isReady,
    router.asPath,
    router.query.eventType,
    router.query.locationType,
    router.query.date,
    sitecoreContext,
    eventTypes,
    locationTypes,
    onFilterChangeCallback,
    onDateFilterChangeCallback,
  ]);

  const onEventTypeClick = (type: EventTypeData) => {
    const exists = selectedEventTypeFilter.find((t) => t.id === type.id);
    const updatedEventTypes = exists
      ? selectedEventTypeFilter.filter((t) => t.id !== type.id)
      : [...selectedEventTypeFilter, type];
    setSelectedEventTypeFilter(updatedEventTypes);
    updateFilters(updatedEventTypes, selectedLocationTypeFilter);
  };

  const onLocationTypeClick = (type: EventTypeData) => {
    const exists = selectedLocationTypeFilter.find((t) => t.id === type.id);
    const updatedLocationTypes = exists
      ? selectedLocationTypeFilter.filter((t) => t.id !== type.id)
      : [...selectedLocationTypeFilter, type];
    setSelectedLocationTypeFilter(updatedLocationTypes);
    updateFilters(selectedEventTypeFilter, updatedLocationTypes);
  };

  return (
    <>
      <div className={cx('head-div', `head-div__active--${filtersActive}`)}>
        <div className={cx('header-row')}>
          <h2>{headerTitle}</h2>
          <div role="status" aria-live="polite" aria-atomic="true">
            <span className={cx('screen-reader-only')}>{headerTitle}</span>
          </div>
          <span className={cx('date-sort')}>{t('SortedByDate')}</span>
        </div>
        <Button className={cx('filter-heading-mobile')} onClick={() => setFiltersActive(!filtersActive)}>
          {t('Filters')}
          <Icon variant={filtersActive ? 'close' : 'filter'} />
        </Button>
        {(selectedDateFilter != '' || selectedEventTypeFilter.length > 0 || selectedLocationTypeFilter.length > 0) && (
          <div className={cx('filter-chips')}>
            {selectedDateFilter != '' && (
              <div className={cx('filter-chip')}>
                <Button onClick={() => removeDateFilter()} aria-label="Clear date flter">
                  {dateFilters.find((f) => f.key === selectedDateFilter)?.value}
                  <Icon variant="close" />
                </Button>
              </div>
            )}
            {selectedEventTypeFilter?.map((type: EventTypeData, index: number) => (
              <div key={index} className={cx('filter-chip')}>
                <Button
                  onClick={() => removeFromFilter(`event-type-${type.id}`)}
                  aria-label={`Remove filter: ${type.name}`}
                >
                  {type.name}
                  <Icon variant="close" />
                </Button>
              </div>
            ))}
            {selectedLocationTypeFilter?.map((type: EventTypeData, index: number) => (
              <div key={index} className={cx('filter-chip')}>
                <Button
                  onClick={() => removeFromFilter(`location-type-${type.id}`)}
                  aria-label={`Remove filter: ${type.name}`}
                >
                  {type.name}
                  <Icon variant="close" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {showNoResultsFound && eventCards?.length > 0 && (
          <div role="status">
            <div className={cx('no-events')}>
              <div className={cx('icon-wrapper')}>
                <Icon variant="calendar-in-circle" />
              </div>
              <strong>{t('NoUpcomingEvents')}</strong>
              <Button
                className={cx('clear-filters-button')}
                onClick={(e) => {
                  e.preventDefault();
                  onClearFilters();
                }}
              >
                {t('Clear Filters')}
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className={cx('filters', `filters__active--${filtersActive}`)}>
        <h2 className={cx('filter-heading-lg')}>{t('Filters')}</h2>
        <div className={cx('event-filters')}>
          {eventsToShow !== 'Past' && (
            <fieldset className={cx('date-filter')}>
              <legend>{t('EventDate')}</legend>
              <ul>
                {dateFilters.map((filter) => (
                  <li key={filter.key}>
                    <input
                      type="radio"
                      id={filter.key}
                      name="eventDateFilter"
                      value={filter.key}
                      checked={selectedDateFilter === filter.key}
                      onChange={handleDateFilterChange}
                    />
                    <label htmlFor={filter.key}>{filter.value}</label>
                  </li>
                ))}
              </ul>
            </fieldset>
          )}
          {eventTypes?.length > 1 && (
            <fieldset className={cx('type-filter')}>
              <legend>{t('EventType')}</legend>
              <ul>
                {eventTypes.slice(0, filtersToShow).map((type, index) => (
                  <li key={`event-type-${index}`}>
                    <input
                      type="checkbox"
                      id={`event-type-${type.id}`}
                      checked={selectedEventTypeFilter.some((t) => t.id === type.id)}
                      onChange={() => onEventTypeClick(type)}
                    />
                    <label htmlFor={`event-type-${type.id}`}>{type.name}</label>
                  </li>
                ))}
              </ul>
              {showViewMore && (
                <a onClick={onViewMoreClick} className={cx('view-more-types')}>
                  {viewAllTypes ? t('ViewLess') : t('ViewMore')}
                  <Icon variant={viewAllTypes ? 'minus' : 'plus'} />
                </a>
              )}
            </fieldset>
          )}
          {locationTypes?.length > 1 && (
            <fieldset className={cx('type-filter')}>
              <legend>{t('LocationType')}</legend>
              <ul>
                {locationTypes.slice(0, filtersToShow).map((type, index) => (
                  <li key={`location-type-${index}`}>
                    <input
                      type="checkbox"
                      id={`location-type-${type.id}`}
                      checked={selectedLocationTypeFilter.some((t) => t.id === type.id)}
                      onChange={() => onLocationTypeClick(type)}
                    />
                    <label htmlFor={`location-type-${type.id}`}>{type.name}</label>
                  </li>
                ))}
              </ul>
            </fieldset>
          )}
          {(selectedDateFilter != '' ||
            selectedEventTypeFilter.length > 0 ||
            selectedLocationTypeFilter.length > 0) && (
            <div className={cx('clear-filters')}>
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  onClearFilters();
                }}
              >
                {t('Clear Filters')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EventListingFilters;
